import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_LOGIN } from '../analytics/events';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  EmailOutlined,
  PhoneOutlined,
  AdminPanelSettingsOutlined,
  SmsOutlined,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { getPortalPath } from '../utils/roleRedirect';
import api from '../utils/api';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';
import AuthCard from '../components/auth/AuthCard';
import AuthSegmentedControl from '../components/auth/AuthSegmentedControl';
import AuthPrimaryButton from '../components/auth/AuthPrimaryButton';
import AuthMarqueeStrip from '../components/auth/AuthMarqueeStrip';
import { formContainer, formField, fieldShake } from '../components/auth/animations';

const TABS = [
  { key: 'student', label: 'Student', icon: <PhoneOutlined sx={{ fontSize: 16 }} /> },
  { key: 'staff', label: 'Staff', icon: <AdminPanelSettingsOutlined sx={{ fontSize: 16 }} /> },
];

/**
 * LoginPage
 * Students: OTP-only login (phone → Send OTP → verify & sign in).
 * Staff: email + password login.
 * Uses AuthLayout (DesktopNav + AuthBackground) as the route-level shell.
 */
function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, otpLogin } = useContext(AuthContext);
  const { track } = useAnalytics();
  const authCardRef = useRef(null);
  const reduced = useReducedMotionPreferred();

  // Preserve redirect info from location state (e.g., from library detail page)
  const from = location.state?.from;
  const pendingPlanId = location.state?.pendingPlanId;

  const [loginMode, setLoginMode] = useState('student');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OTP state (student tab)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Field shake state — keyed by field name
  const [shakeField, setShakeField] = useState(null);

  const handleTabChange = (newValue) => {
    setLoginMode(newValue);
    setFormData({ email: '', phone: '', password: '' });
    setError('');
    setOtpSent(false);
    setOtp('');
  };

  // Cooldown timer for OTP resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOtp = useCallback(async () => {
    if (!formData.phone || formData.phone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      if (!reduced) setShakeField('phone');
      return;
    }
    setError('');
    setSendingOtp(true);
    try {
      const phone = formData.phone.startsWith('+91') ? formData.phone : `+91${formData.phone}`;
      await api.post('/auth/send-otp', { phone, purpose: 'login' });
      setOtpSent(true);
      setResendCooldown(60);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'PHONE_NOT_FOUND') {
        setError('No account found with this phone number');
      } else if (code === 'COOLDOWN') {
        setResendCooldown(err.response?.data?.retryAfter || 60);
        setOtpSent(true);
      } else {
        setError(err.response?.data?.message || 'Failed to send OTP');
      }
    } finally {
      setSendingOtp(false);
    }
  }, [formData.phone, reduced]);

  const handleOtpLogin = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      if (!reduced) setShakeField('otp');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const phone = formData.phone.startsWith('+91') ? formData.phone : `+91${formData.phone}`;
      const loggedInUser = await otpLogin({ phone, otp });
      track(EVT_LOGIN, { method: 'otp' });
      // Pulse success before navigating
      authCardRef.current?.pulse();
      await new Promise((r) => setTimeout(r, 400));
      if (from && pendingPlanId) {
        navigate(from, { state: { pendingPlanId } });
      } else if (from) {
        navigate(from);
      } else {
        const portalPath = getPortalPath(loggedInUser.role);
        navigate(portalPath);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.password) {
      setError('Please enter your password');
      if (!reduced) setShakeField('password');
      return;
    }
    setLoading(true);
    try {
      const credentials = { email: formData.email, password: formData.password };
      const loggedInUser = await login(credentials);
      track(EVT_LOGIN, { method: 'password' });
      // Pulse success before navigating
      authCardRef.current?.pulse();
      await new Promise((r) => setTimeout(r, 400));
      if (from && pendingPlanId) {
        navigate(from, { state: { pendingPlanId } });
      } else if (from) {
        navigate(from);
      } else {
        const portalPath = getPortalPath(loggedInUser.role);
        navigate(portalPath);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      if (!reduced) setShakeField('password');
    } finally {
      setLoading(false);
    }
  };

  // Clear shake after animation completes
  useEffect(() => {
    if (!shakeField) return;
    const t = setTimeout(() => setShakeField(null), 450);
    return () => clearTimeout(t);
  }, [shakeField]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      {/* Main card */}
      <AuthCard
        ref={authCardRef}
        title="Welcome Back"
        subtitle="Sign in to continue to BookMyLibrary"
        maxWidth={440}
      >
        {/* Segmented control: Student / Staff */}
        <Box sx={{ mb: 3 }}>
          <AuthSegmentedControl
            tabs={TABS}
            value={loginMode}
            onChange={handleTabChange}
          />
        </Box>

        {/* Error alert — role="alert" is provided by MUI Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Student tab: OTP-only login */}
        {loginMode === 'student' && (
          <motion.div
            key="student-otp"
            variants={formContainer}
            initial="initial"
            animate="animate"
          >
            {/* Phone field */}
            <motion.div
              variants={formField}
              animate={shakeField === 'phone' ? fieldShake : undefined}
            >
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={otpSent}
                  placeholder="9876543210"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined color="action" />
                        <Typography sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 500 }}>+91</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </motion.div>

            {!otpSent ? (
              <motion.div variants={formField}>
                <AuthPrimaryButton
                  loading={sendingOtp}
                  disabled={!formData.phone}
                  onClick={handleSendOtp}
                  type="button"
                >
                  {sendingOtp ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
                </AuthPrimaryButton>
              </motion.div>
            ) : (
              <motion.form onSubmit={handleOtpLogin}>
                {/* OTP field */}
                <motion.div
                  variants={formField}
                  animate={shakeField === 'otp' ? fieldShake : undefined}
                >
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      placeholder="Enter 6-digit OTP"
                      inputProps={{ maxLength: 6 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SmsOutlined color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        OTP sent to +91{formData.phone}
                      </Typography>
                      {resendCooldown > 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Resend in {resendCooldown}s
                        </Typography>
                      ) : (
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          underline="hover"
                          onClick={handleSendOtp}
                          sx={{ color: '#25D366', fontWeight: 600 }}
                        >
                          Resend OTP
                        </Link>
                      )}
                    </Box>
                  </Box>
                </motion.div>

                <motion.div variants={formField}>
                  <AuthPrimaryButton
                    type="submit"
                    loading={loading}
                    disabled={otp.length !== 6}
                  >
                    {loading ? 'Verifying…' : 'Verify & Sign In'}
                  </AuthPrimaryButton>
                </motion.div>
              </motion.form>
            )}
          </motion.div>
        )}

        {/* Staff tab: email + password login */}
        {loginMode === 'staff' && (
          <motion.form
            key="staff-password"
            onSubmit={handleStaffSubmit}
            variants={formContainer}
            initial="initial"
            animate="animate"
          >
            {/* Email field */}
            <motion.div variants={formField}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </motion.div>

            {/* Password field */}
            <motion.div
              variants={formField}
              animate={shakeField === 'password' ? fieldShake : undefined}
            >
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </motion.div>

            {/* Forgot password link */}
            <motion.div variants={formField}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}
                >
                  Forgot password?
                </Link>
              </Box>
            </motion.div>

            <motion.div variants={formField}>
              <AuthPrimaryButton type="submit" loading={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </AuthPrimaryButton>
            </motion.div>
          </motion.form>
        )}

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            New to BookMyLibrary?
          </Typography>
        </Divider>

        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Link
              component={RouterLink}
              to="/register"
              state={{ from, pendingPlanId }}
              underline="none"
              sx={{ color: 'primary.main', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
            >
              Create one now
            </Link>
          </Typography>
        </Box>
      </AuthCard>

      {/* Desktop marquee strip — positioned alongside the card, desktop ≥lg only */}
      <AuthMarqueeStrip />
    </Box>
  );
}

export default LoginPage;
